import { Loader, Upload } from "lucide-react";
import { ComponentProps, FC, PropsWithChildren, useCallback } from "react";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import type { FileType, UploadedFile } from "@/lib/uploads";

import { FileUploadProvider, useFileUpload } from "./file-upload-context";

type MultiFileUploadButtonProps = PropsWithChildren<{
  fileType: FileType;
  onFinish?: (uploadedFiles: UploadedFile[]) => void;
}>;

export const MultiFileUploadButton: FC<MultiFileUploadButtonProps> = ({
  fileType,
  onFinish,
  children,
}) => {
  return (
    <FileUploadProvider multiple onFinish={onFinish}>
      {children || <FileUploadInputInner fileType={fileType} />}
    </FileUploadProvider>
  );
};

export type FileUploadInputInnerProps = ComponentProps<"input"> & {
  fileType: FileType;
};

const FileUploadInputInner: FC<FileUploadInputInnerProps> = ({ fileType }) => {
  const {
    openFilePicker,
    isUploading,
    uploadedBytes,
    totalBytes,
    totalFileCount,
    currentFileCount,
  } = useFileUpload();

  const handleUploadButtonClick = useCallback(() => {
    openFilePicker(fileType);
  }, [openFilePicker, fileType]);

  if (isUploading && totalBytes > 0) {
    const uploadProgress = Math.round((uploadedBytes / totalBytes) * 100);

    return (
      <Button variant="secondary" disabled className="w-[392px]" type="button">
        <Progress value={uploadProgress} className="animate-pulse" />
        <span className="text-right">
          {uploadProgress}% -{" "}
          {Math.ceil(uploadedBytes / 1024 / 1024).toLocaleString()}/
          {Math.ceil(totalBytes / 1024 / 1024).toLocaleString()}MB - Files{" "}
          {currentFileCount} of {totalFileCount}
        </span>
      </Button>
    );
  }

  return (
    <Button
      onClick={handleUploadButtonClick}
      className="w-[192px]"
      type="button"
      disabled={isUploading}
    >
      {isUploading ? <Loader className="animate-spin" /> : <Upload />} Batch
      Upload Files
    </Button>
  );
};
